import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Ticket, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import AddToCalendar from "@/components/AddToCalendar";

// Buyer's purchased tickets — displayed as QR tokens (rendered via a public
// QR generator so we don't ship a dependency). Long-press or screenshot for
// door check-in.

function qrImgUrl(text, size = 220) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}`;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function MyTickets() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/me/tickets");
        if (!alive) return;
        setItems(r.data?.items || []);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 pb-24" data-testid="my-tickets-page">
      <div className="mb-6">
        <p className="overline text-gray-500">My tickets</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Your ticket wallet</h1>
        <p className="text-gray-600 mt-2 text-[15px]">Show the QR at the door — no printout needed.</p>
      </div>

      {loading ? (
        <p className="text-[#8B7B73]">Loading…</p>
      ) : items.length === 0 ? (
        <Card className="rounded-md p-8 text-center border border-dashed border-[#EAE3DE]" data-testid="my-tickets-empty">
          <Ticket className="mx-auto mb-3 text-gray-400" size={30} />
          <p className="text-sm text-gray-600">
            No tickets yet. <Link to="/events" className="text-[#B31E1E] hover:underline">Browse events</Link>.
          </p>
        </Card>
      ) : (
        <div className="space-y-5">
          {items.map((tk) => (
            <Card key={tk.id} className="rounded-md overflow-hidden flex flex-col md:flex-row" data-testid={`ticket-${tk.id}`}>
              <div className="p-5 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-100 font-mono text-[10px] uppercase tracking-wider">
                    {tk.status}
                  </Badge>
                  {tk.tier_name && (
                    <Badge className="rounded-sm bg-[#B31E1E] text-white hover:bg-[#B31E1E] text-[10px]">{tk.tier_name}</Badge>
                  )}
                </div>
                {tk.concert && (
                  <>
                    <h2 className="mt-2 text-xl font-black text-[#1B1614] tracking-tight">
                      <Link to={`/concerts/${tk.concert.id}`} className="hover:underline">{tk.concert.title}</Link>
                    </h2>
                    <p className="text-sm text-[#5C4E48] mt-1">by {tk.concert.artist_stage_name || "Servrevu artist"}</p>
                    <div className="mt-3 space-y-1 text-sm text-[#5C4E48]">
                      <p className="inline-flex items-center gap-1.5"><Calendar size={13} /> {formatWhen(tk.concert.starts_at)}</p>
                      {tk.concert.venue_name && (
                        <p className="inline-flex items-center gap-1.5"><MapPin size={13} /> {tk.concert.venue_name}{tk.concert.city ? `, ${tk.concert.city}` : ""}</p>
                      )}
                    </div>
                  </>
                )}
                <p className="mt-4 text-[10px] font-mono text-[#8B7B73] break-all">Token: {tk.qr_token}</p>
                {tk.concert && (
                  <div className="mt-3">
                    <AddToCalendar
                      testId={`ticket-cal-${tk.id}`}
                      event={{
                        title: `${tk.concert.title}${tk.concert.artist_stage_name ? ` — ${tk.concert.artist_stage_name}` : ""}`,
                        description: tk.concert.description,
                        location: [tk.concert.venue_name, tk.concert.address, tk.concert.city, tk.concert.state].filter(Boolean).join(", "),
                        starts_at: tk.concert.starts_at,
                        ends_at: tk.concert.ends_at,
                        uid: `ticket-${tk.id}`,
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="bg-[#FAF5F2] border-t md:border-t-0 md:border-l border-[#EAE3DE] p-4 flex items-center justify-center">
                <img
                  src={qrImgUrl(tk.qr_token, 200)}
                  alt="Ticket QR"
                  className="w-40 h-40"
                  data-testid={`ticket-qr-${tk.id}`}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
